import { useEffect, useState } from 'react';
import { Table, Button, Modal } from 'antd';
import { ChartFilter } from '../filters/filter-type';
import { api } from 'src/services';

interface OperatorReport {
  operator: string;
  totalOpened: number;
  machines: string[];
  sector?: string;
  cell?: string;
  descriptions: string[];
}

interface Props {
  filters: ChartFilter;
}

export default function OperatorsReportTable({ filters }: Props) {
  const [data, setData] = useState<OperatorReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [modalMachines, setModalMachines] = useState<string[]>([]);
  const [isMachinesModalVisible, setIsMachinesModalVisible] = useState(false);

  const [modalDescriptions, setModalDescriptions] = useState<string[]>([]);
  const [isDescriptionsModalVisible, setIsDescriptionsModalVisible] = useState(false);

  const pageSize = 10;

  async function fetchReport(page = 1) {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        ...filters,
        page: page.toString(),
        limit: pageSize.toString(),
      } as any).toString();

      const res = await api.get(`/tasks/reports/operators?${params}`);
      setData(res.data.items);
      setTotal(res.data.total);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching operators report', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReport(1);
  }, [filters]);

  const wrapText = (text: string, length = 70) => {
    const regex = new RegExp(`(.{1,${length}})`, 'g');
    return text.match(regex)?.join('\n') || text;
  };

  const columns = [
    { title: 'Operador', dataIndex: 'operator', key: 'operator' },
    { title: 'Setor', dataIndex: 'sector', key: 'sector' },
    { title: 'Célula', dataIndex: 'cell', key: 'cell' },
    { title: 'OS Abertas', dataIndex: 'totalOpened', key: 'totalOpened' },
    {
      title: 'Máquinas',
      dataIndex: 'machines',
      key: 'machines',
      render: (machines: string[]) => (
        <Button
          type="link"
          size="small"
          onClick={() => {
            setModalMachines(machines);
            setIsMachinesModalVisible(true);
          }}
        >
          Ver Máquinas
        </Button>
      ),
    },
    {
      title: 'Descrições',
      dataIndex: 'descriptions',
      key: 'descriptions',
      render: (descriptions: string[]) => (
        <Button
          type="link"
          size="small"
          onClick={() => {
            const formatted = descriptions.map(d => wrapText(d));
            setModalDescriptions(formatted);
            setIsDescriptionsModalVisible(true);
          }}
        >
          Ver Descrições
        </Button>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="operator"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: currentPage,
          pageSize,
          total,
          onChange: (page) => fetchReport(page),
          showSizeChanger: false,
        }}
      />

      <Modal
        title="Máquinas associadas"
        open={isMachinesModalVisible}
        onCancel={() => setIsMachinesModalVisible(false)}
        footer={null}
        width="min(600px, calc(100vw - 32px))"
      >
        {modalMachines.map((m, idx) => (
          <div key={idx}>{m}</div>
        ))}
      </Modal>

      <Modal
        title="Descrições de Chamados"
        open={isDescriptionsModalVisible}
        onCancel={() => setIsDescriptionsModalVisible(false)}
        footer={null}
        width="min(700px, calc(100vw - 32px))"
      >
        {modalDescriptions.map((d, idx) => (
          <p
            key={idx}
            style={{ textAlign: 'justify', whiteSpace: 'pre-wrap', marginBottom: 12 }}
          >
            {d}
          </p>
        ))}
      </Modal>
    </>
  );
}
